import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import imageCompression from 'browser-image-compression';

const ImageCropUpload = ({ onImageProcessed, currentImage, onRemove }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
    aspect: 1, // Proporção 1:1 para logos
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      // Validar tamanho (máx. 10MB antes da compressão)
      if (file.size > 10 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 10MB');
        return;
      }

      setSelectedFile(file);
      
      // Criar preview para crop
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current || !completedCrop.width || !completedCrop.height) {
      // Se não houver crop, usar a imagem original
      return new Promise((resolve) => {
        fetch(imageSrc)
          .then(res => res.blob())
          .then(blob => resolve(blob));
      });
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Calcular dimensões do crop em pixels
    const pixelCrop = {
      x: completedCrop.x * (completedCrop.unit === '%' ? image.width / 100 : 1),
      y: completedCrop.y * (completedCrop.unit === '%' ? image.height / 100 : 1),
      width: completedCrop.width * (completedCrop.unit === '%' ? image.width / 100 : 1),
      height: completedCrop.height * (completedCrop.unit === '%' ? image.height / 100 : 1),
    };
    
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const compressImage = async (imageBlob) => {
    const options = {
      maxSizeMB: 0.5, // Comprimir para no máximo 500KB
      maxWidthOrHeight: 800, // Redimensionar para no máximo 800px
      useWebWorker: true,
      fileType: 'image/jpeg',
    };

    try {
      const compressedFile = await imageCompression(imageBlob, options);
      return compressedFile;
    } catch (error) {
      console.error('Erro ao comprimir imagem:', error);
      return imageBlob;
    }
  };

  const handleCropComplete = async () => {
    try {
      // Obter imagem cortada
      const croppedBlob = await getCroppedImg();
      
      if (!croppedBlob) {
        alert('Erro ao processar a imagem');
        return;
      }

      // Comprimir imagem
      const compressedFile = await compressImage(croppedBlob);

      // Criar nome do arquivo
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, '') + '.jpg';
      
      // Criar um novo File object com o nome correto
      const finalFile = new File([compressedFile], fileName, { type: 'image/jpeg' });

      // Converter para base64 para preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        
        onImageProcessed({
          file: finalFile,
          fileName: fileName,
          preview: base64String,
          contentType: 'image/jpeg',
        });

        // Limpar estados e campo de arquivo
        setShowCropModal(false);
        setImageSrc(null);
        setSelectedFile(null);
        setCompletedCrop(null);
        
        // Limpar o input de arquivo
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(finalFile);
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      alert('Erro ao processar a imagem: ' + error.message);
    }
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setSelectedFile(null);
    setCompletedCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
        />
        {currentImage && (
          <button
            type="button"
            onClick={handleRemoveImage}
            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
          >
            Remover
          </button>
        )}
      </div>

      {currentImage && (
        <div className="mt-2">
          <img
            src={currentImage}
            alt="Logo atual"
            className="max-w-xs max-h-40 object-contain border rounded p-2"
          />
        </div>
      )}

      <p className="text-sm text-gray-500">
        Formatos aceitos: JPG, PNG, GIF (máx. 10MB) • A imagem será automaticamente comprimida e otimizada
      </p>

      {/* Modal de Crop */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold mb-4">Ajustar Imagem</h3>
            
            <div className="mb-4">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  style={{ maxHeight: '60vh', maxWidth: '100%' }}
                />
              </ReactCrop>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Dica:</strong> Arraste as bordas para ajustar a área do logo. 
                A imagem será redimensionada para 800x800px e comprimida automaticamente.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCropComplete}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Aplicar e Comprimir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropUpload;
